import {Trans, useTranslation} from '@/translations/i18n'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {ActivityReportCounters} from '@/features/accesses/activity-report/types'
import {
    StyledActivityReportCounterCard,
    StyledActivityReportCountersGrid,
    StyledActivityReportToValidate
} from './style'

type ActivityReportCountersPanelProps = {
    counters: ActivityReportCounters | undefined
    isLoading: boolean
}

export const ActivityReportCountersPanel = ({counters, isLoading}: ActivityReportCountersPanelProps) => {
    const {t} = useTranslation()

    if (isLoading) {
        return (
            <StyledActivityReportCountersGrid>
                {[0, 1].map(index => (
                    <StyledActivityReportCounterCard key={index}>
                        <Skeleton width={72} height={12} />
                        <Skeleton width={120} height={26} />
                        <Skeleton width={88} height={12} />
                    </StyledActivityReportCounterCard>
                ))}
            </StyledActivityReportCountersGrid>
        )
    }

    if (!counters) return null

    const accessesToValidate = Math.max(0, counters.totalAccesses - counters.validatedAccesses)
    const absencesToValidate = Math.max(0, counters.totalAbsences - counters.validatedAbsences)

    return (
        <StyledActivityReportCountersGrid>
            <StyledActivityReportCounterCard>
                <span className="label">{t('activity_report:counters_accesses')}</span>
                <strong>
                    <Trans
                        i18nKey="activity_report:counters_validated_of_total"
                        components={{i: <i />}}
                        values={{validated: counters.validatedAccesses, total: counters.totalAccesses}}
                    />
                </strong>
                <ToValidateSublabel value={accessesToValidate} />
            </StyledActivityReportCounterCard>
            <StyledActivityReportCounterCard>
                <span className="label">{t('activity_report:counters_absences')}</span>
                <strong>
                    <Trans
                        i18nKey="activity_report:counters_validated_of_total"
                        components={{i: <i />}}
                        values={{validated: counters.validatedAbsences, total: counters.totalAbsences}}
                    />
                </strong>
                <ToValidateSublabel value={absencesToValidate} />
            </StyledActivityReportCounterCard>
        </StyledActivityReportCountersGrid>
    )
}

const ToValidateSublabel = ({value}: {value: number}) => {
    const {t} = useTranslation()

    if (value <= 0) {
        return <span className="sublabel">{t('activity_report:counters_all_validated')}</span>
    }

    return (
        <StyledActivityReportToValidate>
            <AlertCircleIcon size={12} />
            {t('activity_report:counters_to_validate', {value})}
        </StyledActivityReportToValidate>
    )
}
